/**
 * StripeWebhookCard — shows whether Stripe is wired to this site and registers/updates the
 * webhook endpoint (subscription cancellations & failed payments lock accounts automatically).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Copy, CreditCard, KeyRound, Loader2, RefreshCw, Webhook } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StripeWebhookCard() {
  const [siteUrl, setSiteUrl] = useState(window.location.origin);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);
  const [open, setOpen] = useState(false);
  const [secret, setSecret] = useState("");

  const load = async (url = siteUrl) => {
    try { setStatus((await axios.get(`${API}/platform/stripe/webhook`, { params: { site_url: url } })).data); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not read Stripe webhook status"); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const call = async (path, body, okMsg) => {
    setBusy(path);
    try {
      const res = await axios.post(`${API}/platform/stripe/webhook/${path}`, body);
      toast.success(typeof okMsg === "function" ? okMsg(res.data) : okMsg);
      if (res.data.needs_secret) toast.warning("Endpoint exists but this server has no signing secret — paste it from Stripe or rotate.", { duration: 8000 });
      setSecret("");
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Stripe action failed"); } finally { setBusy(null); }
  };

  if (!status) return null;
  const ours = status.endpoints.find((e) => e.is_ours);
  const ok = status.registered;

  return (
    <div className={`rounded-xl border p-4 mb-6 ${ok ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50/40"}`} data-testid="stripe-webhook-card">
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 text-left" data-testid="stripe-webhook-toggle">
          {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
          <Webhook className={`w-5 h-5 ${ok ? "text-emerald-600" : "text-amber-600"}`} />
          <div>
            <p className="font-semibold text-slate-900">Stripe webhook</p>
            <p className="text-xs text-slate-500">Cancellations & failed payments lock agency access automatically</p>
          </div>
        </button>
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          {status.mode && <Badge variant="outline" className={status.mode === "live" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600"} data-testid="stripe-mode">{status.mode === "live" ? "Live keys" : "Test keys"}</Badge>}
          <Badge variant="outline" className={ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"} data-testid="stripe-webhook-status">
            {ok ? <><CheckCircle2 className="w-3 h-3 mr-1" /> Registered</> : <><AlertTriangle className="w-3 h-3 mr-1" /> {ours ? (status.secret_configured ? "Missing events" : "Secret missing") : "Not registered"}</>}
          </Badge>
          <Button size="sm" onClick={() => call("register", { site_url: siteUrl }, (d) => (d.action === "updated" ? "Webhook events updated" : "Webhook registered in Stripe"))} disabled={!!busy || !status.stripe_configured} className="gap-1" data-testid="stripe-webhook-register-btn">
            {busy === "register" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} {ours ? "Update events" : "Register webhook"}
          </Button>
        </div>
      </div>

      {open && (
        <div className="mt-4 grid lg:grid-cols-2 gap-5 text-sm">
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Site URL (where Stripe should send events)</Label>
              <div className="flex gap-2">
                <Input value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} onBlur={() => load(siteUrl)} className="h-8" data-testid="stripe-site-url" />
                <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(status.url); toast.success("Webhook URL copied"); }} className="gap-1" data-testid="stripe-copy-url"><Copy className="w-3.5 h-3.5" /> Copy</Button>
              </div>
              <p className="text-[11px] text-slate-500 mt-1 break-all">Endpoint: {status.url}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-700 mb-1">Required events</p>
              <ul className="grid grid-cols-1 gap-0.5">
                {status.required_events.map((ev) => {
                  const has = ours && !ours.missing_events.includes(ev);
                  return <li key={ev} className={`flex items-center gap-1.5 text-xs ${has ? "text-emerald-700" : "text-slate-500"}`}>{has ? <CheckCircle2 className="w-3 h-3" /> : <span className="w-3 h-3 rounded-full border border-slate-300 inline-block" />}<code>{ev}</code></li>;
                })}
              </ul>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`text-xs ${status.secret_configured ? "text-emerald-700" : "text-amber-700"}`} data-testid="stripe-secret-status">
                <KeyRound className="w-3 h-3 inline mr-1" /> Signing secret {status.secret_configured ? "configured" : "missing"}
              </span>
              <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" disabled={!!busy || !ours} onClick={() => { if (window.confirm("Replace the Stripe endpoint for this site with a new one? The old signing secret stops working immediately.")) call("rotate", { site_url: siteUrl }, "Endpoint replaced — new signing secret saved"); }} data-testid="stripe-webhook-rotate-btn">
                <RefreshCw className="w-3 h-3" /> Rotate secret
              </Button>
            </div>
            <div className="flex gap-2">
              <Input placeholder="Paste whsec_… from Stripe Dashboard" value={secret} onChange={(e) => setSecret(e.target.value)} className="h-8 font-mono text-xs" data-testid="stripe-secret-input" />
              <Button variant="outline" size="sm" disabled={!secret || !!busy} onClick={() => call("secret", { secret }, "Signing secret saved")} data-testid="stripe-secret-save">Save</Button>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <p className="text-xs font-medium text-slate-700 mb-1">Endpoints in your Stripe account</p>
              {status.error && <p className="text-xs text-rose-600">{status.error}</p>}
              {status.endpoints.length === 0 && !status.error && <p className="text-xs text-slate-500 italic">None yet</p>}
              <ul className="space-y-1">
                {status.endpoints.map((e) => (
                  <li key={e.id} className={`rounded border px-2 py-1.5 text-xs ${e.is_ours ? "border-emerald-200 bg-white" : "border-slate-200 bg-white/60"}`} data-testid={`stripe-endpoint-${e.id}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-mono break-all">{e.url}</span>
                      {e.is_ours && <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">this site</Badge>}
                      <Badge variant="outline" className="text-[10px] ml-auto">{e.status}{e.livemode ? " · live" : " · test"}</Badge>
                    </div>
                    {e.missing_events.length > 0 && <p className="text-amber-700 mt-0.5">Missing: {e.missing_events.join(", ")}</p>}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs font-medium text-slate-700 mb-1">Recent events received</p>
              {status.recent_events.length === 0 ? <p className="text-xs text-slate-500 italic" data-testid="stripe-no-events">No events received by this server yet</p> : (
                <ul className="space-y-0.5">
                  {status.recent_events.map((ev, i) => <li key={ev.id || i} className="text-xs text-slate-600 flex gap-2"><span className="text-slate-400">{new Date(ev.received_at).toLocaleString()}</span><code>{ev.type}</code><span className={ev.result === "handled" ? "text-emerald-700" : "text-slate-400"}>{ev.result}</span></li>)}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
